/**
 * ExperienceSection
 *
 * The work-experience entries as a timeline: role, company and place, the
 * period ("Mar 2019 – Present") and the description — three lines, with
 * "Show more" when there is more. Each job used to be a box with no period at
 * all and a description of any length, so a seven-job career was a wall of
 * text with no dates to read it against.
 *
 * `location` is always shaped `{ city?, country? }` (a Places result) — DO
 * NOT render it as a string; compose it from the pair as below.
 *
 * Read-only when `onEdit` is omitted.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import { useLocale, useTranslation } from '@tonnie/locale';
import { Building2, Calendar, MapPin } from '@tonnie/assets';
import { ProfileSectionCard } from '../ProfileSectionCard';
import { formatPeriod } from '../period';
import { IOnboardingData, ProfileReviewEditHandler } from '../types';

interface IExperienceSectionProps {
  data: NonNullable<IOnboardingData['experience']>;
  onEdit?: ProfileReviewEditHandler;
}

export function ExperienceSection({ data, onEdit }: IExperienceSectionProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();

  return (
    <ProfileSectionCard
      icon={Building2}
      title={t('profile.experienceCount', { count: data.length })}
      description={t('profile.professionalWorkExperience')}
      stepId="experience"
      onEdit={onEdit}
    >
      <ol className="relative space-y-5 border-l border-border/60 pl-5">
        {data.map((exp, idx) => {
          const place = [exp.location?.city, exp.location?.country].filter(Boolean).join(', ');
          const period = formatPeriod(
            exp.startDate,
            exp.endDate,
            formatDate,
            t('profile.review.present')
          );
          return (
            <li key={idx} className="relative" data-testid="profile-experience-entry">
              <span
                className="absolute -left-[25px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-background bg-primary"
                aria-hidden="true"
              />
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <p className="text-base font-semibold text-foreground">{exp.role}</p>
                {period && (
                  <p
                    className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground tabular-nums"
                    data-testid="profile-experience-period"
                  >
                    <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                    {period}
                  </p>
                )}
              </div>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                <span className="font-medium">{exp.company}</span>
                {place && (
                  <span className="inline-flex items-center gap-1 text-xs">
                    <MapPin className="h-3 w-3" aria-hidden="true" />
                    {place}
                  </span>
                )}
              </p>
              {exp.description && <ClampedText text={exp.description} />}
            </li>
          );
        })}
      </ol>
    </ProfileSectionCard>
  );
}

/** Three lines, and "Show more" only when the text runs past them. */
function ClampedText({ text }: { text: string }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const node = ref.current;
    if (node && !expanded) setOverflows(node.scrollHeight > node.clientHeight + 1);
  }, [text, expanded]);

  return (
    <div className="mt-2">
      <p
        ref={ref}
        className={
          expanded
            ? 'text-sm leading-relaxed text-muted-foreground whitespace-pre-line'
            : 'text-sm leading-relaxed text-muted-foreground whitespace-pre-line line-clamp-3'
        }
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="mt-1 text-xs font-semibold text-primary-accessible hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          data-testid="profile-experience-toggle"
        >
          {expanded ? t('profile.review.showLess') : t('profile.review.showMore')}
        </button>
      )}
    </div>
  );
}
